import { useCallback, useEffect, useState } from "react";
import { Archive, Cloud, Download, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import { startDownload } from "@/pages/Playlists";

const ITEMS = [
  ["playlists", "Playlists", "as JSON + portable .m3u8"], ["favorite_tracks", "Favourite songs"], ["favorite_albums", "Favourite albums"],
  ["liked", "Thumbs up"], ["disliked", "Thumbs down"], ["play_counts", "Play counts"], ["plays", "Listening history"],
  ["audiobooks", "Audiobook progress"], ["videos", "Video progress"], ["wishlist", "Wishlist"], ["radio_stations", "Radio stations"],
];
const ago = (iso) => {
  if (!iso) return "never";
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  return m < 1 ? "just now" : m < 60 ? `${m} min ago` : m < 1440 ? `${Math.round(m / 60)} h ago` : new Date(iso).toLocaleDateString();
};
const mb = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export const BackupSection = () => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get("/backup").then((r) => setSt(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const download = async () => {
    setBusy("export");
    await startDownload("/backup/export", "Backup download started");
    setTimeout(load, 1500);
    setBusy("");
  };
  const toggleNightly = async (on) => {
    try { await api.put("/backup/prefs", { nightly: on }); setSt((s) => ({ ...s, nightly: on })); toast.success(on ? "Nightly copies on — saved to FileJump at 4:00 UTC" : "Nightly copies off"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const copyNow = async () => {
    setBusy("copy");
    try { const { data } = await api.post("/backup/now"); toast.success(`Saved ${data.file} (${mb(data.bytes)}) to FileJump`); await load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const refreshCopies = async () => {
    setBusy("list");
    try { const { data } = await api.get("/backup/copies"); setSt((s) => ({ ...s, copies: data.copies })); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const fj = st?.filejump;
  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-8" data-testid="backup-section">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-emerald-50 text-emerald-700"><Archive size={18} /></div>
        <div>
          <h2 className="text-lg font-medium">Library backup</h2>
          <p className="text-xs text-zinc-500">Your music files stay where they are — this saves everything you built on top of them, so nothing is lost if the app or the database ever goes away.</p>
        </div>
      </div>
      <ul className="mt-5 grid gap-1.5 sm:grid-cols-2" data-testid="backup-items">
        {ITEMS.map(([k, label, hint]) => (
          <li key={k} className="flex items-baseline justify-between gap-3 rounded-md bg-zinc-50 px-3 py-1.5 text-sm" data-testid={`backup-item-${k}`}>
            <span>{label}{hint && <span className="ml-1 text-[11px] text-zinc-400">{hint}</span>}</span>
            <span className="font-mono text-xs text-zinc-500" data-testid={`backup-count-${k}`}>{st ? (st.counts[k] ?? 0).toLocaleString() : "…"}</span>
          </li>
        ))}
        <li className="flex items-baseline justify-between gap-3 rounded-md bg-zinc-50 px-3 py-1.5 text-sm"><span>Synced settings</span><span className="font-mono text-xs text-zinc-500">theme, EQ, playback</span></li>
      </ul>
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button onClick={download} disabled={busy === "export" || !st} data-testid="backup-download-button"
          className="flex items-center gap-2 rounded-full bg-zinc-900 px-5 py-2 text-sm text-white transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-50">
          {busy === "export" ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Download backup (.zip)
        </button>
        <span className="font-mono text-xs text-zinc-500" data-testid="backup-last-export">Last download: {ago(st?.last_export_at)}</span>
      </div>

      {fj?.available && (
        <div className="mt-8 rounded-md border border-zinc-200 p-5" data-testid="backup-filejump">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Cloud size={16} className="shrink-0 text-sky-700" />
              <div>
                <p className="text-sm font-medium">Nightly copy on FileJump</p>
                <p className="text-xs text-zinc-500">Every night at 4:00 UTC a fresh ZIP goes to <span className="font-mono">{st.folder}/</span> inside your music folder. The newest {st.keep} are kept.</p>
              </div>
            </div>
            <Switch checked={st.nightly} onCheckedChange={toggleNightly} disabled={!fj.connected} data-testid="backup-nightly-switch" aria-label="Nightly backup to FileJump" />
          </div>
          {!fj.connected && <p className="mt-3 text-xs text-amber-700" data-testid="backup-filejump-disconnected">Connect FileJump first (Settings → FileJump) to keep copies there.</p>}
          {st.last_copy && <p className="mt-3 font-mono text-xs text-zinc-500" data-testid="backup-last-copy">Last copy: {st.last_copy.file} · {mb(st.last_copy.bytes)} · {ago(st.last_copy.at)} ({st.last_copy.trigger})</p>}
          {st.last_error && <p className="mt-2 text-xs text-red-700" data-testid="backup-last-error">Last attempt failed {ago(st.last_error.at)}: {st.last_error.message}</p>}
          <div className="mt-4 flex flex-wrap gap-3">
            <button onClick={copyNow} disabled={!fj.connected || busy === "copy"} data-testid="backup-copy-now-button"
              className="flex items-center gap-2 rounded-full border border-zinc-300 px-5 py-2 text-sm transition-colors hover:border-zinc-900 disabled:opacity-50">
              {busy === "copy" ? <Loader2 size={14} className="animate-spin" /> : <Cloud size={14} />} Save a copy now
            </button>
            <button onClick={refreshCopies} disabled={!fj.connected || busy === "list"} data-testid="backup-refresh-copies-button"
              className="flex items-center gap-2 rounded-full px-4 py-2 text-sm text-zinc-600 transition-colors hover:text-zinc-900 disabled:opacity-50">
              <RefreshCw size={14} className={busy === "list" ? "animate-spin" : ""} /> Refresh list
            </button>
          </div>
          {st.copies?.length > 0 && (
            <ul className="mt-4 divide-y divide-zinc-100 rounded-md border border-zinc-100" data-testid="backup-copies">
              {st.copies.map((c) => (
                <li key={c.name} className="flex items-center justify-between px-3 py-1.5 font-mono text-xs" data-testid={`backup-copy-${c.name}`}>
                  <span className="truncate">{c.name}</span><span className="shrink-0 text-zinc-400">{c.size ? mb(c.size) : ""}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
};
